import {
  gameView,
  createGame,
  applyGameMove,
} from '../../../packages/domain/src/50-game-engine.mjs';
import { pebbleCellArtwork, pebbleSceneArtwork } from './60-pebble-artwork.mjs';
import { paperQuarterArtwork, paperSceneArtwork } from './61-paper-artwork.mjs';

const colour = (index) => `var(--game-colour-${index})`;
let serial = 0;
function material(ctx, art, kit, kind, stops) {
  const id = `creative-material-${++serial}`;
  const defs = kit.add(ctx, art, 'defs', {});
  const gradient = kit.add(ctx, defs, kind, {
    id,
    x1: '0%',
    y1: '0%',
    x2: '100%',
    y2: '100%',
    cx: '32%',
    cy: '25%',
    r: '80%',
  });
  for (const [offset, fill, opacity = 1] of stops)
    kit.add(ctx, gradient, 'stop', { offset, 'stop-color': fill, 'stop-opacity': opacity });
  return `url(#${id})`;
}
function identity(ctx, art, shape, kit) {
  const mark = kit.add(ctx, art, 'g', { transform: 'translate(55 56) scale(.38)' });
  kit.symbol(ctx, mark, shape, 'var(--game-night)');
}
function mosaic(ctx, cell, shape, kit) {
  const art = kit.svg(ctx, 'game-piece-art creative-mosaic');
  const filled = Number.isInteger(cell.value) && cell.value >= 0 && cell.value < 6;
  art.setAttribute('data-mosaic-filled', String(filled));
  kit.add(ctx, art, 'rect', {
    x: 5,
    y: 6,
    width: 90,
    height: 90,
    rx: 11,
    fill: 'color-mix(in srgb,var(--game-paper) 20%,var(--game-night))',
    stroke: cell.target ? 'var(--mosaic-ink,var(--game-colour-0))' : 'var(--game-line)',
    'stroke-width': cell.target ? 2.5 : 1,
    ...(cell.target && !filled ? { 'stroke-dasharray': '5 4' } : {}),
  });
  if (filled) {
    art.setAttribute('data-mosaic-colour', String(cell.value));
    const paint = material(ctx, art, kit, 'linearGradient', [
      ['0%', 'color-mix(in srgb,var(--piece-colour) 76%,var(--game-white))'],
      ['30%', 'var(--piece-colour)'],
      ['72%', 'color-mix(in srgb,var(--piece-colour) 90%,var(--game-night))'],
      ['100%', 'color-mix(in srgb,var(--piece-colour) 62%,var(--game-night))'],
    ]);
    const glaze = kit.add(ctx, art, 'g', { 'data-mosaic-glaze': 'true' });
    kit.add(ctx, glaze, 'rect', {
      x: 5,
      y: 6,
      width: 90,
      height: 90,
      rx: 11,
      fill: paint,
      stroke: 'color-mix(in srgb,var(--piece-colour) 72%,var(--game-white))',
      'stroke-width': 1.5,
    });
    kit.path(ctx, glaze, 'M14 13H80Q88 13 88 21V29L13 47V21Q13 13 14 13Z', {
      fill: 'var(--game-white)',
      opacity: 0.15,
    });
    kit.path(ctx, glaze, 'M12 78Q12 89 23 89H79Q88 89 88 80', {
      fill: 'none',
      stroke: 'var(--game-night)',
      'stroke-width': 3,
      opacity: 0.28,
    });
    const mark = kit.add(ctx, glaze, 'g', {
      transform: 'translate(59 60) scale(.3)',
      opacity: 0.65,
    });
    kit.symbol(ctx, mark, shape, 'var(--game-night)');
  } else if (cell.target) {
    kit.circle(ctx, art, 50, 50, 5, {
      fill: 'var(--mosaic-ink,var(--game-colour-0))',
      opacity: 0.6,
    });
  }
  kit.path(ctx, art, 'M15 16H76M12 22V54', {
    fill: 'none',
    stroke: 'var(--game-white)',
    'stroke-width': 2,
    'stroke-linecap': 'round',
    opacity: filled ? 0.55 : 0.2,
  });
  return art;
}
/** A preview simulates one canonical centre impression on fresh private paper.
 * Configuration comes from the visible scene. Invalid tools return no footprint;
 * accepted board, RNG, moves, completion and existing marks are never touched. */
export function patternStampFootprint(configuration, stamp = configuration?.stamp) {
  if (
    !configuration ||
    !Number.isInteger(stamp) ||
    stamp < 0 ||
    stamp > 3 ||
    !Number.isInteger(configuration.rotation) ||
    configuration.rotation < 0 ||
    configuration.rotation > 3 ||
    !Number.isInteger(configuration.ink) ||
    configuration.ink < 0 ||
    configuration.ink > 5 ||
    typeof configuration.symmetry !== 'boolean'
  )
    return [];
  const preview = createGame('pattern-stamp', 0);
  Object.assign(preview.data, {
    stamp,
    rotation: configuration.rotation,
    ink: configuration.ink,
    symmetry: configuration.symmetry,
  });
  return applyGameMove(preview, { type: 'cell', index: 14 }).data.board.flatMap((value, index) =>
    value >= 0 ? [index] : [],
  );
}
function stamp(ctx, state, cell, shape, kit) {
  const art = kit.svg(ctx, 'game-piece-art creative-stamp');
  kit.add(ctx, art, 'rect', { width: 100, height: 100, fill: 'var(--game-paper)' });
  for (let fibre = 0; fibre < 8; fibre++)
    kit.path(ctx, art, `M${8 + fibre * 12} 0Q${fibre * 12} 50 ${12 + fibre * 12} 100`, {
      fill: 'none',
      stroke: 'var(--game-paper-muted)',
      'stroke-width': 0.6,
      opacity: 0.35,
    });
  if (cell.value < 0) return art;
  const ink = material(ctx, art, kit, 'linearGradient', [
    ['0%', 'color-mix(in srgb,var(--piece-colour) 75%,var(--game-white))'],
    ['25%', 'var(--piece-colour)'],
    ['65%', 'var(--piece-colour)'],
    ['100%', 'color-mix(in srgb,var(--piece-colour) 72%,var(--game-night))'],
  ]);
  // The established board stores colour only; current tool is not retroactively
  // imputed to old impressions. Each colour receives a stable print silhouette.
  const mark = kit.add(ctx, art, 'g', {
    transform: `rotate(${(cell.value % 4) * 90} 50 50)`,
    'data-print-impression': cell.value,
  });
  if (cell.value % 3 === 0) {
    for (const [x, y] of [
      [50, 28],
      [72, 50],
      [50, 72],
      [28, 50],
    ])
      kit.add(ctx, mark, 'ellipse', {
        cx: x,
        cy: y,
        rx: 14,
        ry: 22,
        fill: ink,
        opacity: 0.82,
        transform: `rotate(${x === 50 ? 0 : 90} ${x} ${y})`,
      });
    kit.circle(ctx, mark, 50, 50, 9, { fill: 'var(--game-colour-3)' });
  } else if (cell.value % 3 === 1) {
    kit.path(
      ctx,
      mark,
      'M26 80Q48 46 64 19M43 58Q17 52 21 34Q40 34 43 58M51 43Q78 47 84 28Q65 23 51 43',
      { fill: ink, stroke: ink, 'stroke-width': 3, 'stroke-linecap': 'round', opacity: 0.9 },
    );
  } else {
    kit.add(ctx, mark, 'rect', {
      x: 20,
      y: 20,
      width: 60,
      height: 60,
      rx: 10,
      fill: ink,
      opacity: 0.85,
      transform: 'rotate(15 50 50)',
    });
    kit.circle(ctx, mark, 50, 50, 18, { fill: 'var(--game-white)', opacity: 0.15 });
  }
  identity(ctx, art, shape, kit);
  return art;
}
function thread(ctx, cell, kit) {
  const art = kit.svg(ctx, 'game-piece-art creative-thread', '0 0 100 100');
  art.setAttribute('data-weave-over', cell.over ? 'warp' : 'weft');
  // Broad strands meet neighbouring cells at exact edges. Tiny fibre channels
  // retain textile texture without turning each accepted crossing into an icon.
  kit.add(ctx, art, 'rect', {
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    fill: colour(cell.weft),
    opacity: 0.62,
  });
  const strand = (vertical, value, top) => {
    const group = kit.add(ctx, art, 'g', {
      'data-thread-axis': vertical ? 'warp' : 'weft',
      'data-thread-colour': value,
      'data-thread-top': String(top),
    });
    const id = `creative-thread-${++serial}`;
    const defs = kit.add(ctx, art, 'defs', {});
    const fill = kit.add(ctx, defs, 'linearGradient', {
      id,
      x1: '0%',
      y1: '0%',
      x2: vertical ? '100%' : '0%',
      y2: vertical ? '0%' : '100%',
    });
    for (const [offset, paint, opacity] of [
      ['0%', 'color-mix(in srgb,var(--game-night) 65%,' + colour(value) + ')', 1],
      ['9%', colour(value), 1],
      ['17%', 'color-mix(in srgb,var(--game-white) 35%,' + colour(value) + ')', 1],
      ['32%', colour(value), 1],
      ['42%', 'color-mix(in srgb,var(--game-white) 50%,' + colour(value) + ')', 1],
      ['56%', colour(value), 1],
      ['93%', colour(value), 1],
      ['100%', 'color-mix(in srgb,var(--game-night) 65%,' + colour(value) + ')', 1],
    ])
      kit.add(ctx, fill, 'stop', { offset, 'stop-color': paint, 'stop-opacity': opacity });
    const geometry = vertical
      ? { x: 4, y: 0, width: 92, height: 100 }
      : { x: 0, y: 4, width: 100, height: 92 };
    kit.add(ctx, group, 'rect', {
      ...geometry,
      fill: `url(#${id})`,
      'data-thread-span': 'edge-to-edge',
    });
    for (let i = 0; i < 11; i++) {
      const at = 9 + i * 8;
      kit.path(ctx, group, vertical ? `M${at} 0V100` : `M0 ${at}H100`, {
        stroke: i % 2 ? 'var(--game-white)' : 'var(--game-night)',
        opacity: i % 2 ? 0.2 : 0.16,
        'stroke-width': 1.2,
      });
    }
    if (top) {
      group.setAttribute('class', 'loom-top-strand');
      kit.path(
        ctx,
        group,
        vertical ? 'M19 0Q29 50 19 100M79 0Q69 50 79 100' : 'M0 19Q50 29 100 19M0 79Q50 69 100 79',
        {
          class: 'loom-silk-sheen',
          fill: 'none',
          stroke: 'var(--game-white)',
          'stroke-width': 1.2,
          opacity: 0.28,
        },
      );
      kit.path(ctx, group, vertical ? 'M4 0V100M96 0V100' : 'M0 4H100M0 96H100', {
        stroke: 'var(--game-night)',
        'stroke-width': 1.5,
        opacity: 0.55,
      });
    }
  };
  strand(!cell.over, cell.over ? cell.weft : cell.warp, false);
  strand(cell.over, cell.over ? cell.warp : cell.weft, true);
  return art;
}

function sand(ctx, cell, kit) {
  const art = kit.svg(ctx, 'game-piece-art creative-sand', '0 0 300 360');
  const glass = material(ctx, art, kit, 'linearGradient', [
    ['0%', 'var(--game-white)', 0.6],
    ['5%', 'var(--game-white)', 0.15],
    ['12%', 'var(--game-colour-4)', 0.05],
    ['83%', 'var(--game-colour-4)', 0.05],
    ['93%', 'var(--game-white)', 0.25],
    ['100%', 'var(--game-white)', 0.6],
  ]);
  kit.add(ctx, art, 'rect', {
    x: 22,
    y: 10,
    width: 256,
    height: 340,
    rx: 24,
    fill: glass,
    stroke: 'var(--game-colour-4)',
    'stroke-width': 3,
  });
  for (const [index, value] of cell.layers.entries()) {
    const y = 337 - index * 12;
    const band = kit.add(ctx, art, 'g', { 'data-sand-band': index, 'data-sand-colour': value });
    kit.path(ctx, band, `M29 ${y}Q90 ${y - 5} 151 ${y}T271 ${y}V${y + 12}H29Z`, {
      fill: colour(value),
    });
    kit.path(ctx, band, `M29 ${y}Q90 ${y - 5} 151 ${y}T271 ${y}`, {
      fill: 'none',
      stroke: 'var(--game-white)',
      'stroke-width': 0.7,
      opacity: 0.35,
    });
    for (let grain = 0; grain < 22; grain++)
      kit.circle(
        ctx,
        band,
        34 + ((grain * 37 + index * 11) % 232),
        y + 2 + ((grain * 7) % 8),
        0.45 + (grain % 3) * 0.3,
        {
          fill: grain % 3 ? 'var(--game-white)' : 'var(--game-night)',
          opacity: grain % 3 ? 0.38 : 0.2,
        },
      );
  }
  kit.path(ctx, art, 'M38 31V318M259 31V318', {
    stroke: 'var(--game-white)',
    'stroke-width': 4,
    opacity: 0.4,
    'stroke-linecap': 'round',
  });
  kit.add(ctx, art, 'ellipse', {
    cx: 150,
    cy: 13,
    rx: 126,
    ry: 8,
    fill: 'none',
    stroke: 'var(--game-white)',
    'stroke-width': 3,
    opacity: 0.7,
  });
  return art;
}

/** Original materials decorate canonical derived cells without changing moves,
 * hidden state, selection or grid positions. The canonical SVG toolkit is injected. */
export function creativeCellArtwork(ctx, state, cell, index, shape, text, kit) {
  switch (state.gameId) {
    case 'mosaic-paint':
      return mosaic(ctx, cell, shape, kit);
    case 'paper-fold':
      return paperQuarterArtwork(ctx, cell, kit, shape);
    case 'pattern-stamp':
      return stamp(ctx, state, cell, shape, kit);
    case 'thread-loom':
      return thread(ctx, cell, kit);
    case 'sand-layers':
      return sand(ctx, cell, kit);
    case 'pebble-balance':
      return pebbleCellArtwork(ctx, cell, {
        ...kit,
        material: (host, kind, stops) => material(ctx, host, kit, kind, stops),
      });
    case 'cloud-shapes':
    case 'ripple-garden':
      return kit.svg(ctx, 'game-piece-art creative-touch');
    default:
      return null;
  }
}
function pond(ctx, art, scene, kit) {
  art.setAttribute('data-pond-theme', String(scene.theme));
  const water = material(ctx, art, kit, 'radialGradient', [
    ['0%', colour([0, 4, 5][scene.theme])],
    ['42%', colour([4, 2, 2][scene.theme])],
    ['100%', 'var(--game-night)'],
  ]);
  kit.add(ctx, art, 'rect', { width: 360, height: 360, rx: 24, fill: water });
  const reflection = material(ctx, art, kit, 'radialGradient', [
    ['0%', 'var(--game-white)', 0.65],
    ['25%', colour(scene.theme === 1 ? 4 : 3), 0.2],
    ['100%', 'var(--game-white)', 0],
  ]);
  kit.add(ctx, art, 'ellipse', { cx: 108, cy: 62, rx: 116, ry: 86, fill: reflection });
  for (let i = 0; i < 12; i++) {
    const d = `M-20 ${28 + i * 29}Q85 ${5 + i * 29} 180 ${28 + i * 29}T380 ${28 + i * 29}`;
    kit.path(ctx, art, d, {
      class: 'pond-reflection',
      fill: 'none',
      stroke: 'var(--game-night)',
      opacity: 0.12,
      'stroke-width': 5,
    });
    kit.path(ctx, art, d, {
      fill: 'none',
      stroke: 'var(--game-white)',
      opacity: 0.095,
      'stroke-width': 1.3,
    });
  }
  const leaf = material(ctx, art, kit, 'radialGradient', [
    ['0%', 'color-mix(in srgb,var(--game-colour-0) 70%,var(--game-white))'],
    ['50%', colour(0)],
    ['100%', 'color-mix(in srgb,var(--game-colour-0) 50%,var(--game-night))'],
  ]);
  for (const [x, y, r] of [
    [38, 72, 23],
    [310, 263, 28],
    [66, 298, 17],
  ]) {
    kit.add(ctx, art, 'ellipse', {
      cx: x + 3,
      cy: y + 5,
      rx: r,
      ry: r * 0.85,
      fill: 'var(--game-night)',
      opacity: 0.4,
    });
    kit.path(
      ctx,
      art,
      `M${x} ${y}L${x + r} ${y + 3}A${r} ${r} 0 1 1 ${x + r * 0.88} ${y - r * 0.48}Z`,
      { class: 'pond-lily', fill: leaf, stroke: colour(0), 'stroke-width': 1 },
    );
    for (let petal = 0; petal < 5; petal++)
      kit.add(ctx, art, 'ellipse', {
        cx: x,
        cy: y - r / 2,
        rx: 5,
        ry: 10,
        fill: colour(scene.theme === 2 ? 1 : 5),
        stroke: 'var(--game-white)',
        'stroke-width': 0.7,
        'stroke-opacity': 0.6,
        transform: `rotate(${petal * 72} ${x} ${y})`,
      });
    kit.circle(ctx, art, x, y, 4, {
      fill: colour(3),
      stroke: 'var(--game-white)',
      'stroke-width': 0.8,
    });
  }
  for (const ring of scene.rings) {
    const x = 36 + ring.x * 72,
      y = 36 + ring.y * 72;
    const group = kit.add(ctx, art, 'g', {
      'data-serial': ring.serial,
      'data-ripple-x': x,
      'data-ripple-y': y,
      class: 'creative-water-ring',
    });
    const waves = kit.add(ctx, group, 'g', { class: 'pond-waves' });
    for (const [r, opacity] of [
      [18, 0.25],
      [36, 0.16],
      [54, 0.09],
    ]) {
      kit.circle(ctx, waves, x, y, r, {
        class: 'pond-ring-shadow',
        fill: 'none',
        stroke: 'var(--game-night)',
        'stroke-width': 2.5,
        opacity: opacity * 0.75,
      });
      kit.circle(ctx, waves, x, y, r, {
        class: 'pond-ring-light',
        fill: 'none',
        stroke: 'var(--game-white)',
        'stroke-width': 1.2,
        opacity,
      });
    }
    kit.circle(ctx, group, x, y, 4, {
      class: 'pond-pebble',
      fill: colour(3),
      stroke: 'var(--game-white)',
      'stroke-width': 1,
      opacity: 0,
    });
  }
}

function clouds(ctx, art, scene, kit) {
  art.setAttribute('data-cloud-sky', String(scene.sky));
  const sky = material(ctx, art, kit, 'linearGradient', [
    ['0%', colour([4, 2, 1][scene.sky])],
    ['58%', 'color-mix(in srgb,' + colour([0, 5, 3][scene.sky]) + ' 55%,var(--game-white))'],
    ['100%', colour([0, 5, 3][scene.sky])],
  ]);
  kit.add(ctx, art, 'rect', { width: 360, height: 360, rx: 24, fill: sky });
  const sun = material(ctx, art, kit, 'radialGradient', [
    ['0%', 'var(--game-white)', 0.8],
    ['35%', colour(3), 0.45],
    ['100%', colour(3), 0],
  ]);
  kit.circle(ctx, art, 289, 61, 65, { fill: sun });
  const id = `creative-cloud-union-${++serial}`,
    defs = kit.add(ctx, art, 'defs', {});
  const silhouette = kit.add(ctx, defs, 'clipPath', { id });
  for (let index = 0; index < scene.board.length; index++) {
    if (!scene.board[index]) continue;
    const x = 30 + (index % 6) * 60,
      y = 30 + Math.floor(index / 6) * 60;
    for (const [dx, dy, r] of [
      [-17, 5, 23],
      [0, -8, 31],
      [21, 6, 24],
    ])
      kit.circle(ctx, silhouette, x + dx, y + dy, r, { 'data-cloud-silhouette': index });
  }
  const fluff = material(ctx, art, kit, 'linearGradient', [
    ['0%', 'var(--game-white)'],
    ['38%', 'var(--game-white)'],
    ['72%', 'color-mix(in srgb,var(--game-white) 88%,' + colour([4, 2, 1][scene.sky]) + ')'],
    ['100%', 'color-mix(in srgb,var(--game-white) 70%,' + colour([4, 2, 1][scene.sky]) + ')'],
  ]);
  const body = kit.add(ctx, art, 'g', { class: 'cloud-continuous-body' });
  kit.add(ctx, body, 'rect', {
    width: 360,
    height: 360,
    fill: fluff,
    'clip-path': `url(#${id})`,
  });
  const sheen = material(ctx, art, kit, 'radialGradient', [
    ['0%', 'var(--game-white)', 0.45],
    ['100%', 'var(--game-white)', 0],
  ]);
  for (let index = 0; index < scene.board.length; index++)
    if (scene.board[index]) {
      const x = 30 + (index % 6) * 60,
        y = 30 + Math.floor(index / 6) * 60;
      const cluster = kit.add(ctx, art, 'g', {
        'data-cloud-cell': index,
        'data-cloud-x': x,
        'data-cloud-y': y,
        'clip-path': `url(#${id})`,
      });
      kit.add(ctx, cluster, 'ellipse', { cx: x - 7, cy: y - 12, rx: 28, ry: 23, fill: sheen });
    }
}

/** Scene artwork consumes only the canonical public view. Existing interactive
 * buttons remain the sole hit surface; no scene changes game state or reshuffles it. */
export function creativeSceneArtwork(ctx, host, state, kit) {
  if (
    !['ripple-garden', 'cloud-shapes', 'paper-fold', 'thread-loom', 'pebble-balance'].includes(
      state.gameId,
    )
  )
    return false;
  const view = gameView(state),
    scene = view.scene;
  const art = kit.svg(ctx, 'game-scene-art creative-world', '0 0 360 360');
  host.dataset.game = state.gameId;
  host.setAttribute('aria-hidden', 'true');
  if (state.gameId === 'ripple-garden') {
    pond(ctx, art, scene, kit);
    host.gameSceneSerial = scene.serial;
  }
  if (state.gameId === 'cloud-shapes') clouds(ctx, art, scene, kit);
  if (state.gameId === 'paper-fold') paperSceneArtwork(ctx, art, scene, kit);
  if (state.gameId === 'thread-loom') {
    kit.add(ctx, art, 'rect', {
      x: 7,
      y: 7,
      width: 346,
      height: 346,
      rx: 18,
      fill: 'var(--game-night)',
      stroke: colour(3),
      'stroke-width': 10,
    });
    for (let i = 0; i < 6; i++) {
      kit.path(ctx, art, `M${32 + i * 59} 0V360`, {
        stroke: colour(scene.columns[i]),
        'stroke-width': 5,
      });
      kit.path(ctx, art, `M0 ${32 + i * 59}H360`, {
        stroke: colour(scene.rows[i]),
        'stroke-width': 5,
      });
    }
  }
  if (state.gameId === 'pebble-balance')
    pebbleSceneArtwork(ctx, art, state, scene, {
      ...kit,
      material: (host, kind, stops) => material(ctx, host, kit, kind, stops),
    });

  host.replaceChildren(art);
  return true;
}
